import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { addMonths, format, parseISO } from 'date-fns';
import { useSnackbar } from 'notistack';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from '../../auth/useAuth';
import { useRBAC } from '../../auth/useRBAC';
import { patientChartControls } from '../../auth/pageControls';
import { downloadAuthedBlob, useAuthedBlobUrl } from '../../hooks/useAuthedBlobUrl';
import { useClinicTimezone } from '../../hooks/useClinicTimezone';
import { stableDocsSharesKey } from '../../hooks/useStableDocsShares';
import { calendarDayKey, formatCalendarDate, formatInstantInZone } from '../../utils/dateUtils';
import { sendErrorMessage } from '../../utils/sendErrorMessage';
import {
  Candidate,
  ConsentMethod,
  PURPOSES,
  PURPOSE_LABELS,
  Purpose,
  RECORD_TYPES,
  RECORD_TYPE_LABELS,
  RangeKind,
  RecipientType,
  RecordType,
  SUMMARY_LABELS,
  SUMMARY_SECTIONS,
  SummarySection,
  exportDownloadPath,
  stableDocsService,
} from '../../services/stabledocsService';

const STEPS = ['Who and why', 'What to send', 'Preview', 'Send'];

const RANGE_LABELS: Record<RangeKind, string> = {
  last_visit: 'Last visit',
  '3m': '3 months',
  '6m': '6 months',
  '12m': '12 months',
  all: 'All time',
  custom: 'Custom',
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MB = 1024 * 1024;
/** A build normally takes seconds; past this the dialog stops polling and offers a retry. */
const SLOW_BUILD_MS = 120_000;
const keyOf = (c: { type: string; id: string }) => `${c.type}:${c.id}`;
const parseEmails = (s: string) => s.split(/[,;\s]+/).filter(Boolean);
const fmtMb = (bytes: number) => `${(bytes / MB).toFixed(1)} MB`;

/** The calendar-day bounds of a range preset. "Today" is the clinic's day. */
function rangeBounds(range: RangeKind, today: string, lastVisit: string | null, from: string, to: string) {
  const back = (n: number) => format(addMonths(parseISO(today), -n), 'yyyy-MM-dd');
  switch (range) {
    case '3m': return { from: back(3), to: undefined };
    case '6m': return { from: back(6), to: undefined };
    case '12m': return { from: back(12), to: undefined };
    case 'last_visit': return lastVisit ? { from: lastVisit, to: lastVisit } : {};
    case 'custom': return { from: from || undefined, to: to || undefined };
    default: return {};
  }
}

export interface ShareRecordsDialogProps {
  open: boolean;
  onClose: () => void;
  horseId: string;
  horseName?: string;
}

const ShareRecordsDialog: React.FC<ShareRecordsDialogProps> = (props) =>
  props.open ? <Wizard {...props} /> : null;

const Wizard: React.FC<ShareRecordsDialogProps> = ({ onClose, horseId, horseName }) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const tz = useClinicTimezone();
  const { user } = useAuth();
  const rbac = useRBAC();
  const can = patientChartControls(rbac);

  const [step, setStep] = useState(0);

  // Step 1
  const [purpose, setPurpose] = useState<Purpose>('referral');
  const [recipientType, setRecipientType] = useState<RecipientType>('owner');
  // Index into `owners`: an owner known only by name on the horse has no client id to key on.
  const [ownerIdx, setOwnerIdx] = useState(-1);
  const [recipientName, setRecipientName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [consentAck, setConsentAck] = useState(false);
  // The address an owner-send consent was confirmed for: typing a different one asks again.
  const [ownerConsentFor, setOwnerConsentFor] = useState('');
  const [consentMethod, setConsentMethod] = useState<ConsentMethod | ''>('');
  const [consentDocId, setConsentDocId] = useState('');
  const [consentFileName, setConsentFileName] = useState('');
  const [uploading, setUploading] = useState(false);

  // Step 2
  const [range, setRange] = useState<RangeKind>('12m');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [enabledTypes, setEnabledTypes] = useState<Set<RecordType>>(new Set(RECORD_TYPES));
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [includeAttachments, setIncludeAttachments] = useState(false);
  const [includeDrafts, setIncludeDrafts] = useState(false);
  const [summary, setSummary] = useState<Set<SummarySection>>(new Set());
  // What the person has ticked or unticked by hand. A filter change only default-ticks items they
  // never touched; a purpose change (a fresh choice) clears both.
  const manualOn = useRef(new Set<string>());
  const manualOff = useRef(new Set<string>());
  const [resetKey, setResetKey] = useState(0);

  // Step 3 / 4
  const [exportId, setExportId] = useState<string | null>(null);
  const [slow, setSlow] = useState(false);
  const [cc, setCc] = useState(user?.email ?? '');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const draftApplied = useRef<string | null>(null);

  // The practice's consent wording and presets: nothing starts until they are in, so a slow or
  // failed read can never put placeholder wording in front of the sender.
  const settingsQuery = useQuery(['stabledocs', 'settings'], stableDocsService.getSettings, {
    staleTime: 60_000,
    retry: false,
  });
  const settings = settingsQuery.data;
  const candidatesQuery = useQuery(
    ['stabledocs', 'candidates', horseId, includeDrafts],
    () => stableDocsService.getCandidates(horseId, includeDrafts),
    { keepPreviousData: true },
  );
  const items = useMemo(() => candidatesQuery.data?.items ?? [], [candidatesQuery.data]);
  const owners = useMemo(() => candidatesQuery.data?.owners ?? [], [candidatesQuery.data]);
  const loaded = !!settings && !!candidatesQuery.data;

  // Purpose applies its preset (range, record types, summary sections).
  useEffect(() => {
    const preset = settings?.presets?.[purpose];
    if (!preset) return;
    setSummary(new Set(preset.summarySections));
    setRange(preset.range);
    setEnabledTypes(new Set(preset.types));
  }, [purpose, settings]);

  // The owner is prefilled; with several owners the first is picked and the sender can change it.
  useEffect(() => {
    if (recipientType === 'owner' && ownerIdx < 0 && owners.length) pickOwner(0);
  }, [owners, recipientType]);

  function pickOwner(idx: number) {
    const o = owners[idx];
    setOwnerIdx(idx);
    setRecipientName(o?.name ?? '');
    setRecipientEmail(o?.email ?? '');
  }

  function pickRecipientType(t: RecipientType) {
    setRecipientType(t);
    if (t === 'owner') {
      if (owners.length) pickOwner(ownerIdx >= 0 ? ownerIdx : 0);
    } else {
      setOwnerIdx(-1);
      setRecipientName('');
      setRecipientEmail('');
    }
  }

  const today = formatInstantInZone(new Date(), tz, 'yyyy-MM-dd');
  const dayOf = (c: Candidate) => calendarDayKey(c.date, tz);

  const lastVisit = useMemo(() => {
    const days = (items.some((i) => i.type === 'soap') ? items.filter((i) => i.type === 'soap') : items)
      .map((i) => calendarDayKey(i.date, tz))
      .filter(Boolean)
      .sort();
    return days.length ? days[days.length - 1] : null;
  }, [items, tz]);

  const bounds = rangeBounds(range, today, lastVisit, customFrom, customTo);
  const inRange = (c: Candidate) => {
    const d = dayOf(c);
    return (!bounds.from || d >= bounds.from) && (!bounds.to || d <= bounds.to);
  };
  const inRangeItems = items.filter(inRange);
  const visible = inRangeItems.filter((c) => enabledTypes.has(c.type));

  // Changing the range, the record types or the data ticks what is newly in scope; anything the
  // person ticked or unticked by hand keeps their choice.
  const scopeSig = visible.map(keyOf).join('|');
  useEffect(() => {
    if (!candidatesQuery.data) return;
    const inScope = new Set(visible.map(keyOf));
    const keep = (k: string) => inScope.has(k) && !manualOff.current.has(k);
    setSelected(new Set(visible.map(keyOf).filter(keep)));
  }, [scopeSig, resetKey]);

  const countOf = (t: RecordType) => inRangeItems.filter((c) => c.type === t).length;
  // Only what is on screen is sent: a type switched off takes its ticked records with it.
  const selectedItems = visible.filter((c) => selected.has(keyOf(c)));
  const attachedBytes = includeAttachments ? selectedItems.reduce((n, c) => n + (c.sizeBytes ?? 0), 0) : 0;

  // Switching a type on or off only narrows or widens what is shown.
  const toggleType = (t: RecordType) => {
    setEnabledTypes((prev) => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t); else next.add(t);
      return next;
    });
  };
  const remember = (k: string, on: boolean) => {
    (on ? manualOn : manualOff).current.add(k);
    (on ? manualOff : manualOn).current.delete(k);
  };
  const setTypeSelection = (t: RecordType, on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      visible.filter((c) => c.type === t).forEach((c) => {
        remember(keyOf(c), on);
        if (on) next.add(keyOf(c)); else next.delete(keyOf(c));
      });
      return next;
    });
  const toggleItem = (c: Candidate) => {
    const on = !selected.has(keyOf(c));
    remember(keyOf(c), on);
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(keyOf(c)); else next.delete(keyOf(c));
      return next;
    });
  };
  const toggleSummary = (s: SummarySection) =>
    setSummary((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s); else next.add(s);
      return next;
    });

  // ---- step 1 rules ----
  const emailOk = EMAIL_RE.test(recipientEmail.trim());
  // An owner send to an address not on file for any owner of this horse (To, or a CC other than the sender's
  // own) needs the sender to confirm consent; the server applies the same rule.
  const typedEmail = recipientEmail.trim().toLowerCase();
  const onFile = new Set(owners.flatMap((o) => o.emails ?? []));
  const myEmail = (user?.email ?? '').trim().toLowerCase();
  const ccList = parseEmails(cc);
  const offFile =
    recipientType !== 'owner'
      ? []
      : Array.from(
          new Set([
            ...(emailOk && !onFile.has(typedEmail) ? [typedEmail] : []),
            ...ccList.map((e) => e.toLowerCase()).filter((e) => !onFile.has(e) && e !== myEmail),
          ]),
        ).sort();
  const ownerEmailNotOnFile = offFile.length > 0;
  // The tick is for exactly these addresses: changing one asks again.
  const ownerConsentGiven = ownerEmailNotOnFile && ownerConsentFor === offFile.join(',');
  const ownerConsentOk = !ownerEmailNotOnFile || ownerConsentGiven;
  const ownerOk = recipientType === 'owner' && (owners.length === 0 || ownerIdx >= 0) && emailOk && ownerConsentOk;
  const vetOk = recipientType === 'vet' && !!recipientName.trim() && emailOk;
  const otherOk =
    recipientType === 'other' && !!recipientName.trim() && emailOk && consentAck && !!consentMethod &&
    (consentMethod !== 'signed_form' || !!consentDocId);
  const step0Ok = ownerOk || vetOk || otherOk;
  const step1Ok = selectedItems.length > 0;

  const uploadForm = async (file: File | undefined) => {
    if (!file) return;
    setUploading(true);
    try {
      const doc = await stableDocsService.uploadConsentForm(horseId, file);
      setConsentDocId(doc.id);
      setConsentFileName(file.name);
    } catch (e) {
      enqueueSnackbar(sendErrorMessage(e, 'Could not upload the form').message, { variant: 'error' });
    } finally {
      setUploading(false);
    }
  };

  // ---- step 3: create the PDF, then poll until it is ready or failed ----
  const createExport = useMutation(
    () =>
      stableDocsService.createExport(horseId, {
        purpose,
        dateFrom: bounds.from,
        dateTo: bounds.to,
        items: selectedItems.map((c) => ({ type: c.type, id: c.id })),
        includeAttachments,
        includeDrafts: includeDrafts && can.shareDrafts,
        summarySections: SUMMARY_SECTIONS.filter((s) => summary.has(s)),
        recipientName: recipientName.trim() || undefined,
      }),
    { onSuccess: (view) => setExportId(view.id) },
  );
  // A build left queued (worker switched off) or killed mid-way would otherwise poll forever.
  useEffect(() => {
    setSlow(false);
    if (!exportId) return undefined;
    const timer = setTimeout(() => setSlow(true), SLOW_BUILD_MS);
    return () => clearTimeout(timer);
  }, [exportId]);
  const exportQuery = useQuery(
    ['stabledocs', 'export', exportId],
    () => stableDocsService.getExport(exportId!),
    {
      enabled: !!exportId,
      refetchInterval: (data) => (slow || (data && (data.status === 'ready' || data.status === 'failed')) ? false : 1500),
    },
  );
  const exp = exportQuery.data;
  const ready = exp?.status === 'ready';
  const failed = exp?.status === 'failed';
  const pdfUrl = useAuthedBlobUrl(ready && exportId ? `${exportDownloadPath(exportId)}?preview=1` : null);
  const limitMb = settings?.attachThresholdMb ?? 0;
  const overEmailLimit = ready && (exp?.fileSizeBytes ?? 0) > limitMb * MB;

  // The email starts from the purpose template once, when the PDF is ready.
  useEffect(() => {
    if (exp?.emailDraft && draftApplied.current !== exp.id) {
      draftApplied.current = exp.id;
      setSubject(exp.emailDraft.subject);
      setBody(exp.emailDraft.body);
    }
  }, [exp]);

  const ccOk = ccList.length <= 5 && ccList.every((e) => EMAIL_RE.test(e));
  const sendOk = ready && !overEmailLimit && emailOk && ownerConsentOk && ccOk && !!subject.trim() && !!body.trim();

  const send = useMutation(
    () =>
      stableDocsService.send(exportId!, {
        recipientType,
        recipientEmail: recipientEmail.trim(),
        recipientName: recipientName.trim() || undefined,
        ccEmails: ccList.length ? ccList : undefined,
        subject: subject.trim(),
        body,
        authorizingOwnerId: recipientType === 'owner' ? owners[ownerIdx]?.clientId ?? undefined : undefined,
        consentAcknowledged: recipientType === 'other' ? consentAck : ownerEmailNotOnFile ? ownerConsentGiven : undefined,
        consentMethod: recipientType === 'other' && consentMethod ? consentMethod : undefined,
        consentDocId: recipientType === 'other' && consentMethod === 'signed_form' ? consentDocId : undefined,
      }),
    {
      onSuccess: () => {
        enqueueSnackbar('Records sent', { variant: 'success' });
        queryClient.invalidateQueries(stableDocsSharesKey(horseId));
        // The send is also a Client Comms entry.
        queryClient.invalidateQueries(['client-communications', horseId]);
        onClose();
      },
      onError: (e) => {
        const f = sendErrorMessage(e, 'Could not send the records');
        enqueueSnackbar(f.message, { variant: f.variant });
        // A timeout may still have sent it: show whatever the server recorded.
        queryClient.invalidateQueries(stableDocsSharesKey(horseId));
      },
    },
  );

  const goNext = () => {
    if (step === 1) {
      setExportId(null);
      createExport.mutate();
    }
    setStep((s) => s + 1);
  };
  const retry = () => {
    setExportId(null);
    createExport.mutate();
  };

  const canNext = !loaded ? false : step === 0 ? step0Ok : step === 1 ? step1Ok : step === 2 ? ready : false;

  // ---- grouped item list (step 2) ----
  const groups = useMemo(() => {
    const byDay = new Map<string, Candidate[]>();
    visible.forEach((c) => {
      const d = calendarDayKey(c.date, tz);
      byDay.set(d, [...(byDay.get(d) ?? []), c]);
    });
    return Array.from(byDay.entries()).sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [scopeSig, tz]);

  const ownerConsentBlock = ownerEmailNotOnFile ? (
    <Alert severity="warning" data-testid="owner-email-consent">
      <Typography variant="body2" sx={{ mb: 0.5 }}>
        We don&apos;t have {offFile.join(', ')} on file for this owner. If these records are not going to the owner,
        confirm you have consent to send them there.
      </Typography>
      <FormControlLabel
        control={
          <Checkbox
            size="small"
            checked={ownerConsentGiven}
            onChange={(e) => setOwnerConsentFor(e.target.checked ? offFile.join(',') : '')}
          />
        }
        label={`I have consent to send these records to ${offFile.length === 1 ? 'this email' : 'these emails'}`}
      />
    </Alert>
  ) : null;

  const body0 = (
    <Stack spacing={2.5}>
      <TextField
        select
        label="Purpose"
        value={purpose}
        onChange={(e) => {
          manualOn.current.clear();
          manualOff.current.clear();
          setResetKey((n) => n + 1);
          setPurpose(e.target.value as Purpose);
        }}
        size="small"
        fullWidth
      >
        {PURPOSES.map((p) => (
          <MenuItem key={p} value={p}>{PURPOSE_LABELS[p]}</MenuItem>
        ))}
      </TextField>

      <Box>
        <Typography variant="subtitle2" gutterBottom>Send to</Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={recipientType}
          onChange={(_, v) => v && pickRecipientType(v)}
          aria-label="Recipient type"
        >
          <ToggleButton value="owner">Owner</ToggleButton>
          <ToggleButton value="vet">Vet</ToggleButton>
          <ToggleButton value="other">Other</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {recipientType === 'owner' && owners.length > 1 && (
        <TextField
          select
          label="Owner"
          data-testid="share-owner"
          value={ownerIdx < 0 ? '' : String(ownerIdx)}
          onChange={(e) => pickOwner(Number(e.target.value))}
          size="small"
          fullWidth
        >
          {owners.map((o, i) => (
            <MenuItem key={o.clientId ?? `name-${i}`} value={String(i)}>{o.name}</MenuItem>
          ))}
        </TextField>
      )}
      {recipientType === 'owner' && owners.length === 0 && candidatesQuery.data && (
        <Typography variant="body2" color="text.secondary">This patient has no owner on file. Type an email below.</Typography>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          label="Name"
          value={recipientName}
          onChange={(e) => setRecipientName(e.target.value)}
          size="small"
          fullWidth
          required={recipientType !== 'owner'}
        />
        <TextField
          label="Email"
          type="email"
          value={recipientEmail}
          onChange={(e) => setRecipientEmail(e.target.value)}
          size="small"
          fullWidth
          required
          error={!!recipientEmail && !emailOk}
        />
      </Stack>
      {ownerConsentBlock}

      {recipientType === 'other' && (
        <Stack spacing={1.5} data-testid="consent-block">
          <FormControlLabel
            control={<Checkbox checked={consentAck} onChange={(e) => setConsentAck(e.target.checked)} />}
            label={settings?.consentText ?? ''}
          />
          <TextField
            select
            label="How was it given?"
            data-testid="consent-method"
            value={consentMethod}
            onChange={(e) => setConsentMethod(e.target.value as ConsentMethod)}
            size="small"
            fullWidth
          >
            <MenuItem value="verbal">Verbal</MenuItem>
            <MenuItem value="written">Written</MenuItem>
            {can.uploadFile && <MenuItem value="signed_form">Signed form</MenuItem>}
          </TextField>
          {consentMethod === 'signed_form' && (
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Button component="label" size="small" variant="outlined" disabled={uploading}>
                {consentDocId ? 'Replace form' : 'Upload signed form'}
                <input
                  hidden
                  type="file"
                  aria-label="Signed form file"
                  onChange={(e) => uploadForm(e.target.files?.[0])}
                />
              </Button>
              {uploading && <CircularProgress size={18} />}
              {consentFileName && <Typography variant="body2">{consentFileName}</Typography>}
            </Stack>
          )}
        </Stack>
      )}
    </Stack>
  );

  const body1 = (
    <Stack spacing={2.5}>
      <Box>
        <Typography variant="subtitle2" gutterBottom>Dates</Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={range}
          onChange={(_, v) => { if (v) setRange(v); }}
          aria-label="Date range"
          sx={{ flexWrap: 'wrap' }}
        >
          {(Object.keys(RANGE_LABELS) as RangeKind[]).map((r) => (
            <ToggleButton key={r} value={r}>{RANGE_LABELS[r]}</ToggleButton>
          ))}
        </ToggleButtonGroup>
        {range === 'custom' && (
          <Stack direction="row" spacing={2} sx={{ mt: 1.5 }}>
            <TextField
              label="From"
              type="date"
              size="small"
              value={customFrom}
              onChange={(e) => setCustomFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="To"
              type="date"
              size="small"
              value={customTo}
              onChange={(e) => setCustomTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        )}
      </Box>

      <Box>
        <Typography variant="subtitle2" gutterBottom>Records</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 2 }}>
          {RECORD_TYPES.map((t) => (
            <Box key={t} sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel
                sx={{ flexGrow: 1, mr: 0 }}
                control={<Checkbox size="small" checked={enabledTypes.has(t)} onChange={() => toggleType(t)} />}
                label={`${RECORD_TYPE_LABELS[t]} (${countOf(t)})`}
              />
              {enabledTypes.has(t) && countOf(t) > 0 && (
                <>
                  <Button size="small" onClick={() => setTypeSelection(t, true)} data-testid={`select-all-${t}`} aria-label={`Select all ${RECORD_TYPE_LABELS[t]}`}>Select all</Button>
                  <Button size="small" onClick={() => setTypeSelection(t, false)} aria-label={`Clear ${RECORD_TYPE_LABELS[t]}`}>Clear</Button>
                </>
              )}
            </Box>
          ))}
        </Box>
      </Box>

      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, maxHeight: 280, overflowY: 'auto' }}>
        {candidatesQuery.isLoading && <Box sx={{ p: 2 }}><CircularProgress size={20} /></Box>}
        {!candidatesQuery.isLoading && groups.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>Nothing to send in this range.</Typography>
        )}
        {groups.map(([day, rows]) => (
          <Box key={day}>
            <Typography variant="caption" sx={{ display: 'block', px: 1.5, py: 0.5, bgcolor: 'action.hover', fontWeight: 600 }}>
              {formatCalendarDate(day, 'MMM d, yyyy')}
            </Typography>
            {rows.map((c) => (
              <FormControlLabel
                key={keyOf(c)}
                sx={{ display: 'flex', px: 1, mr: 0 }}
                control={<Checkbox size="small" checked={selected.has(keyOf(c))} onChange={() => toggleItem(c)} />}
                label={
                  <Box>
                    <Typography variant="body2">
                      {c.title}
                      {c.isDraft ? ' (draft)' : ''}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {[RECORD_TYPE_LABELS[c.type], c.subtitle].filter(Boolean).join(' · ')}
                    </Typography>
                  </Box>
                }
              />
            ))}
          </Box>
        ))}
      </Box>

      <Box>
        <FormControlLabel
          control={<Checkbox size="small" checked={includeAttachments} onChange={(e) => setIncludeAttachments(e.target.checked)} />}
          label="Include attached files"
        />
        {can.shareDrafts && (
          <FormControlLabel
            control={<Checkbox size="small" checked={includeDrafts} onChange={(e) => setIncludeDrafts(e.target.checked)} />}
            label="Include drafts"
          />
        )}
      </Box>

      <Box>
        <Typography variant="subtitle2" gutterBottom>Summary page</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
          {SUMMARY_SECTIONS.map((s) => (
            <FormControlLabel
              key={s}
              control={<Checkbox size="small" checked={summary.has(s)} onChange={() => toggleSummary(s)} />}
              label={SUMMARY_LABELS[s]}
            />
          ))}
        </Box>
      </Box>

      <Typography variant="body2" color="text.secondary" data-testid="running-total">
        {selectedItems.length} {selectedItems.length === 1 ? 'item' : 'items'}
        {attachedBytes > 0 ? ` · ${fmtMb(attachedBytes)} of files` : ''}
      </Typography>
    </Stack>
  );

  const retryButton = <Button color="inherit" size="small" onClick={retry} data-testid="share-retry">Try again</Button>;
  const overLimitAlert = overEmailLimit && exp?.fileSizeBytes ? (
    <Alert severity="warning" data-testid="share-over-limit">
      This PDF is {fmtMb(exp.fileSizeBytes)}, over your {limitMb} MB email limit. Download it to send another way, or go
      back and narrow the dates or leave out attached files.
    </Alert>
  ) : null;

  const body2 = (
    <Stack spacing={2}>
      {createExport.isError && (
        <Alert severity="error" action={retryButton}>
          {sendErrorMessage(createExport.error, 'Could not start the PDF').message}
        </Alert>
      )}
      {failed && (
        <Alert severity="error" action={retryButton}>
          {exp?.errorMessage || 'The PDF could not be made.'}
        </Alert>
      )}
      {slow && !ready && !failed && (
        <Alert severity="warning" action={retryButton}>
          This is taking longer than usual.
        </Alert>
      )}
      {overLimitAlert}
      {!ready && !failed && !slow && !createExport.isError && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 6, justifyContent: 'center' }}>
          <CircularProgress size={22} />
          <Typography variant="body2">Making the PDF...</Typography>
        </Box>
      )}
      {ready && exp && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ flexGrow: 1 }}>
              {exp.pageCount ?? '?'} pages
              {exp.fileSizeBytes ? ` · ${fmtMb(exp.fileSizeBytes)}` : ''}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() =>
                downloadAuthedBlob(exportDownloadPath(exp.id), exp.fileName || 'records.pdf').catch(() =>
                  enqueueSnackbar('Could not download the PDF', { variant: 'error' }),
                )
              }
            >
              Download
            </Button>
          </Box>
          {pdfUrl ? (
            <Box component="iframe" title="PDF preview" src={pdfUrl} sx={{ width: '100%', height: 460, border: '1px solid', borderColor: 'divider', borderRadius: 1 }} />
          ) : (
            <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress size={22} /></Box>
          )}
        </>
      )}
    </Stack>
  );

  const body3 = (
    <Stack spacing={2}>
      {overLimitAlert}
      <TextField label="To" size="small" fullWidth value={recipientEmail} onChange={(e) => setRecipientEmail(e.target.value)} error={!emailOk} />
      {ownerConsentBlock}
      <TextField
        label="CC"
        size="small"
        fullWidth
        value={cc}
        onChange={(e) => setCc(e.target.value)}
        error={!ccOk}
        helperText="Separate several with commas. Up to 5."
      />
      <TextField label="Subject" size="small" fullWidth value={subject} onChange={(e) => setSubject(e.target.value)} />
      <TextField label="Message" fullWidth multiline minRows={8} value={body} onChange={(e) => setBody(e.target.value)} />
      <Typography variant="body2" color="text.secondary">
        Sent as an attachment: {exp?.fileName ?? 'records.pdf'}
        {exp?.fileSizeBytes ? ` (${fmtMb(exp.fileSizeBytes)})` : ''}
      </Typography>
    </Stack>
  );

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md" PaperProps={{ sx: { minHeight: '70vh' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6" component="span" fontWeight={700}>
          StableDocs{horseName ? `: ${horseName}` : ''}
        </Typography>
        <IconButton size="small" onClick={onClose} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Box sx={{ px: 3, pt: 2, pb: 1, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Stepper activeStep={step} alternativeLabel>
          {STEPS.map((label) => (
            <Step key={label}><StepLabel>{label}</StepLabel></Step>
          ))}
        </Stepper>
      </Box>

      <DialogContent sx={{ px: 3, py: 3 }}>
        {(settingsQuery.isError || candidatesQuery.isError) && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  if (settingsQuery.isError) settingsQuery.refetch();
                  if (candidatesQuery.isError) candidatesQuery.refetch();
                }}
              >
                Try again
              </Button>
            }
          >
            Could not load this patient's records. Check the connection and try again.
          </Alert>
        )}
        {!loaded && !settingsQuery.isError && !candidatesQuery.isError && (
          <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress size={22} /></Box>
        )}
        {loaded && step === 0 && body0}
        {loaded && step === 1 && body1}
        {step === 2 && body2}
        {step === 3 && body3}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
        {step > 0 && (
          <Button onClick={() => setStep((s) => s - 1)} disabled={send.isLoading || createExport.isLoading} sx={{ mr: 'auto' }} data-testid="share-back">
            Back
          </Button>
        )}
        <Button onClick={onClose}>Cancel</Button>
        {step < 3 ? (
          <Button variant="contained" onClick={goNext} disabled={!canNext} data-testid="share-next">
            Next
          </Button>
        ) : (
          <Button variant="contained" onClick={() => send.mutate()} disabled={!sendOk || send.isLoading} data-testid="share-send">
            Send
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ShareRecordsDialog;
